import {
  knowledgeBaseEntriesRoute,
  type KnowledgeBaseSubject,
} from "helpers/knowledge-base";
import { useGET } from "hooks/useAPI";

/**
 * The tags the knowledge base defines. Mirrors after-internal's
 * KnowledgeBaseTag — asking for one it does not know is rejected upstream and
 * the page renders empty, so these must stay in step.
 */
export const KNOWLEDGE_BASE_TAGS = {
  faq: "faq",
  deathCertificates: "death-certificates",
  localPickup: "local-pickup",
  timeline: "timeline",
  fees: "fees",
} as const;

export type KnowledgeBaseTag =
  (typeof KNOWLEDGE_BASE_TAGS)[keyof typeof KNOWLEDGE_BASE_TAGS];

/**
 * A per-situation fact attached to an answer — a pickup address, a phone
 * number, opening hours. `type` decides how it renders; `label` is authored.
 */
export type KnowledgeBaseField = {
  key: string;
  label: string;
  type: string;
  /**
   * An address arrives as its postal parts, so it can be rendered on its own
   * lines. Every other type is a scalar.
   */
  value: string | number | boolean | Record<string, string>;
};

export type KnowledgeBaseEntry = {
  entry_key: string;
  /** Null when the entry belongs to no section; it reads after those that do. */
  category: string | null;
  title: string;
  body_html: string;
  fields: KnowledgeBaseField[];
};

export type KnowledgeBaseCategory = {
  slug: string;
  label: string;
};

export type KnowledgeBaseResponse = {
  categories: KnowledgeBaseCategory[];
  entries: KnowledgeBaseEntry[];
};

const swrConfig = {
  shouldRetryOnError: (error: { statusCode?: number }) =>
    error?.statusCode !== 404,
};

/**
 * The knowledge base as this case or policy sees it: one variant per entry,
 * already resolved, ordered, and interpolated server-side. Nothing here
 * decides which answer applies — that is the point of the contract.
 */
export function useKnowledgeBase(
  subject: KnowledgeBaseSubject | null,
  tags?: readonly KnowledgeBaseTag[],
) {
  const { data, isLoading, error } = useGET(
    subject ? knowledgeBaseEntriesRoute(subject, tags) : null,
    undefined,
    swrConfig,
  );

  const typedData = data as KnowledgeBaseResponse | undefined;

  return {
    categories: typedData?.categories ?? [],
    entries: typedData?.entries ?? [],
    isLoading,
    error,
  };
}

/** The entries of one category, in the order the response listed them. */
export function entriesInCategory(
  entries: readonly KnowledgeBaseEntry[],
  slug: string,
): KnowledgeBaseEntry[] {
  return entries.filter((entry) => entry.category === slug);
}

/**
 * Entries belonging to no section. They read after the sections that have one —
 * without this they would be fetched and then silently never shown, since
 * nothing would match them to a heading.
 */
export function uncategorisedEntries(
  entries: readonly KnowledgeBaseEntry[],
): KnowledgeBaseEntry[] {
  return entries.filter((entry) => entry.category === null);
}
